import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { QRCodeSVG } from 'qrcode.react';
import BottomNavBar from '../components/BottomNavBar';
import SEOMeta from '../components/SEOMeta';
import LoginPromptModal from '../components/LoginPromptModal';
import { useAuth } from '../contexts/AuthContext';

type PaymentMethod = 'qris' | 'va' | 'akulaku';
type PaymentStatus = 'idle' | 'loading' | 'pending' | 'paid' | 'expired' | 'failed';

interface PaymentData {
  orderId: string;
  method: PaymentMethod;
  qrisUrl?: string;
  qrisString?: string;
  vaNumber?: string;
  vaBank?: string;
  redirectUrl?: string;
  totalAmount: number;
  expiresAt: string;
}

const PRESET_AMOUNTS = [10000, 25000, 50000, 100000, 200000, 500000];

export default function DonatePage() {
  const { user, loading: authLoading } = useAuth();
  const [showLogin, setShowLogin] = useState(false);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<PaymentMethod>('qris');
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>('idle');
  const [paymentData, setPaymentData] = useState<PaymentData | null>(null);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState('');

  // Check auth
  useEffect(() => {
    if (!authLoading && !user) {
      setShowLogin(true);
    }
  }, [authLoading, user]);

  // Poll status every 5 seconds when pending
  useEffect(() => {
    if (!paymentData || paymentStatus !== 'pending') return;
    const interval = setInterval(checkStatus, 5000);
    return () => clearInterval(interval);
  }, [paymentData, paymentStatus]);

  // Countdown timer
  useEffect(() => {
    if (!paymentData?.expiresAt || paymentStatus !== 'pending') return;

    const updateCountdown = () => {
      const now = Date.now();
      const expires = new Date(paymentData.expiresAt).getTime();
      const diff = expires - now;

      if (diff <= 0) {
        setPaymentStatus('expired');
        setCountdown('00:00:00');
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const secs = Math.floor((diff % (1000 * 60)) / 1000);
      setCountdown(
        `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
      );
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [paymentData?.expiresAt, paymentStatus]);

  const numAmount = Number(amount.replace(/\D/g, ''));

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '');
    setAmount(raw);
  };

  const selectPreset = (val: number) => {
    setAmount(String(val));
  };

  const createPayment = async () => {
    if (!user) {
      setShowLogin(true);
      return;
    }
    if (numAmount < 10000) {
      setError('Minimum pembayaran Rp 10.000');
      return;
    }

    try {
      setPaymentStatus('loading');
      setError('');

      const res = await fetch('/api/payments/donate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ amount: numAmount, paymentMethod: method }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Gagal membuat pembayaran');
        setPaymentStatus('idle');
        return;
      }

      setPaymentData(data);
      setPaymentStatus('pending');
    } catch (err) {
      setError('Terjadi kesalahan jaringan');
      setPaymentStatus('idle');
    }
  };

  const checkStatus = async () => {
    if (!paymentData) return;
    try {
      const res = await fetch(`/api/payments/donate/status/${paymentData.orderId}`, {
        credentials: 'include',
      });
      const data = await res.json();

      if (data.status === 'paid') {
        setPaymentStatus('paid');
      } else if (data.status === 'expired') {
        setPaymentStatus('expired');
      } else if (data.status === 'failed') {
        setPaymentStatus('failed');
      }
    } catch (err) {
      console.error('Status check failed:', err);
    }
  };

  const resetPayment = () => {
    setPaymentStatus('idle');
    setPaymentData(null);
    setError('');
  };

  const formatCurrency = (val: number) =>
    `Rp ${val.toLocaleString('id-ID')}`;

  // Render payment result (QRIS / VA / Akulaku)
  const renderPaymentUI = () => {
    if (!paymentData) return null;

    if (paymentData.method === 'akulaku') {
      // Auto redirect to payment page
      if (paymentData.redirectUrl) {
        window.location.href = paymentData.redirectUrl;
      }
      return (
        <>
          <Title>Pembayaran Cicilan</Title>
          <Subtitle>Mengalihkan ke halaman pembayaran...</Subtitle>
          <StateBox style={{ padding: '40px 20px' }}>
            <Spinner />
            <StateText>Mohon tunggu sebentar...</StateText>
          </StateBox>
          {paymentData.redirectUrl && (
            <PayButton onClick={() => window.location.href = paymentData.redirectUrl!}>
              Buka Akulaku
            </PayButton>
          )}
        </>
      );
    }

    if (paymentData.method === 'va') {
      return (
        <>
          <Title>Transfer via VA BCA</Title>
          <Subtitle>Transfer ke nomor Virtual Account berikut</Subtitle>

          <VABox>
            <VABankLabel>Bank BCA</VABankLabel>
            <VANumberDisplay>{paymentData.vaNumber || '-'}</VANumberDisplay>
            <CopyButton onClick={() => {
              if (paymentData.vaNumber) {
                navigator.clipboard.writeText(paymentData.vaNumber);
              }
            }}>Salin Nomor VA</CopyButton>
          </VABox>

          <AmountBox>
            <AmountLabel>Total Transfer</AmountLabel>
            <AmountValue>{formatCurrency(paymentData.totalAmount)}</AmountValue>
          </AmountBox>

          <InfoBox>
            <InfoIcon>💡</InfoIcon>
            <InfoText>
              Transfer tepat sesuai nominal. Pembayaran otomatis terverifikasi dalam 1-5 menit.
            </InfoText>
          </InfoBox>

          <VASteps>
            <StepTitle>Cara Transfer:</StepTitle>
            <Step>1. Buka aplikasi BCA Mobile / KlikBCA</Step>
            <Step>2. Pilih menu Transfer {'>'} Virtual Account</Step>
            <Step>3. Masukkan nomor VA di atas</Step>
            <Step>4. Konfirmasi dan selesaikan pembayaran</Step>
          </VASteps>
        </>
      );
    }

    // QRIS
    return (
      <>
        <Title>Scan QRIS</Title>
        <Subtitle>Gunakan aplikasi e-wallet atau mobile banking</Subtitle>

        <QRISBox>
          {paymentData.qrisString ? (
            <QRCodeSVG value={paymentData.qrisString} size={220} level="M" includeMargin />
          ) : paymentData.qrisUrl ? (
            <QRISImage src={paymentData.qrisUrl} alt="QRIS" />
          ) : (
            <QRISPlaceholder>QR Code tidak tersedia</QRISPlaceholder>
          )}
        </QRISBox>

        <AmountBox>
          <AmountLabel>Total Pembayaran</AmountLabel>
          <AmountValue>{formatCurrency(paymentData.totalAmount)}</AmountValue>
        </AmountBox>

        <InfoBox>
          <InfoIcon>💡</InfoIcon>
          <InfoText>
            Pembayaran otomatis terverifikasi dalam 1-2 menit setelah transfer
          </InfoText>
        </InfoBox>

        <SupportedApps>
          <SupportedLabel>Didukung oleh:</SupportedLabel>
          <AppList>
            <AppBadge>GoPay</AppBadge>
            <AppBadge>OVO</AppBadge>
            <AppBadge>DANA</AppBadge>
            <AppBadge>ShopeePay</AppBadge>
            <AppBadge>LinkAja</AppBadge>
          </AppList>
        </SupportedApps>
      </>
    );
  };

  return (
    <PageWrapper>
      <SEOMeta
        title="Donasi - Kindergame"
        description="Dukung Kindergame dengan donasi"
      />

      <Header>
        <HeaderTitle>Donasi</HeaderTitle>
      </Header>

      <Content>
        {paymentStatus === 'loading' ? (
          <StateBox>
            <Spinner />
            <StateText>Menyiapkan pembayaran...</StateText>
          </StateBox>
        ) : paymentStatus === 'paid' ? (
          <StateBox>
            <StateIcon>🎉</StateIcon>
            <StateTitle style={{ color: '#10b981' }}>Pembayaran Berhasil!</StateTitle>
            <StateText>Terima kasih atas donasi kamu</StateText>
            <RetryButton onClick={resetPayment} style={{ marginTop: 24 }}>Donasi Lagi</RetryButton>
          </StateBox>
        ) : paymentStatus === 'expired' ? (
          <StateBox>
            <StateIcon>⏰</StateIcon>
            <StateTitle>Pembayaran Expired</StateTitle>
            <StateText>Waktu pembayaran habis</StateText>
            <RetryButton onClick={resetPayment}>Buat Pembayaran Baru</RetryButton>
          </StateBox>
        ) : paymentStatus === 'failed' ? (
          <StateBox>
            <StateIcon>❌</StateIcon>
            <StateTitle>Pembayaran Gagal</StateTitle>
            <RetryButton onClick={resetPayment}>Coba Lagi</RetryButton>
          </StateBox>
        ) : paymentStatus === 'pending' ? (
          <PaymentSection>
            {renderPaymentUI()}

            <StatusBar>
              <PulsingDot />
              <StatusText>Menunggu pembayaran...</StatusText>
              <CountdownText>{countdown}</CountdownText>
            </StatusBar>

            <OrderInfo>
              Order ID: <strong>{paymentData?.orderId}</strong>
            </OrderInfo>

            <CancelButton onClick={resetPayment}>Batalkan</CancelButton>
          </PaymentSection>
        ) : (
          /* idle - input form */
          <>
            <HeroSection>
              <HeroIcon>❤️</HeroIcon>
              <HeroTitle>Dukung Kindergame</HeroTitle>
              <HeroText>
                Bantu kami terus mengembangkan konten edukatif untuk anak-anak Indonesia
              </HeroText>
            </HeroSection>

            <FormSection>
              <Label>Nominal Donasi</Label>
              <AmountInput
                type="text"
                inputMode="numeric"
                placeholder="Masukkan nominal"
                value={amount ? `Rp ${Number(amount).toLocaleString('id-ID')}` : ''}
                onChange={handleAmountChange}
              />
              {numAmount > 0 && numAmount < 10000 && (
                <ErrorHint>Minimum Rp 10.000</ErrorHint>
              )}

              <PresetGrid>
                {PRESET_AMOUNTS.map((val) => (
                  <PresetButton
                    key={val}
                    $active={numAmount === val}
                    onClick={() => selectPreset(val)}
                  >
                    {formatCurrency(val)}
                  </PresetButton>
                ))}
              </PresetGrid>

              <Label style={{ marginTop: 24 }}>Metode Pembayaran</Label>
              <MethodGrid>
                <MethodCard $active={method === 'qris'} onClick={() => setMethod('qris')}>
                  <MethodIcon>📱</MethodIcon>
                  <MethodName>QRIS</MethodName>
                  <MethodDesc>E-wallet & mobile banking</MethodDesc>
                </MethodCard>
                <MethodCard $active={method === 'va'} onClick={() => setMethod('va')}>
                  <MethodIcon>🏦</MethodIcon>
                  <MethodName>VA BCA</MethodName>
                  <MethodDesc>Transfer bank virtual account</MethodDesc>
                </MethodCard>
                <MethodCard $active={method === 'akulaku'} onClick={() => setMethod('akulaku')}>
                  <MethodIcon>💳</MethodIcon>
                  <MethodName>Cicilan</MethodName>
                  <MethodDesc>Akulaku PayLater</MethodDesc>
                </MethodCard>
              </MethodGrid>

              {error && <ErrorBox>{error}</ErrorBox>}

              <PayButton
                disabled={numAmount < 10000}
                onClick={createPayment}
              >
                Lanjutkan Pembayaran
              </PayButton>
            </FormSection>
          </>
        )}
      </Content>

      {showLogin && (
        <LoginPromptModal
          subtitle="Silakan login untuk melanjutkan donasi"
          onClose={() => setShowLogin(false)}
        />
      )}

      <BottomNavBar />
    </PageWrapper>
  );
}

/* ── Styled Components ── */

const PageWrapper = styled.div`
  max-width: 512px;
  margin: 0 auto;
  min-height: 100dvh;
  background: #f6f8f8;
  font-family: 'Plus Jakarta Sans', sans-serif;
  padding-bottom: 96px;
`;

const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 50;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid #f1f5f9;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HeaderTitle = styled.h2`
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
`;

const Content = styled.main`
  padding: 0 20px 20px;
`;

/* Hero */
const HeroSection = styled.section`
  text-align: center;
  padding: 32px 0 24px;
`;

const HeroIcon = styled.div`
  font-size: 48px;
  margin-bottom: 12px;
`;

const HeroTitle = styled.h1`
  font-size: 24px;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 8px;
`;

const HeroText = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0;
  line-height: 1.6;
`;

/* Form */
const FormSection = styled.section`
  background: white;
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 700;
  color: #475569;
  margin-bottom: 10px;
`;

const AmountInput = styled.input`
  width: 100%;
  height: 56px;
  border: 2px solid #e2e8f0;
  border-radius: 14px;
  padding: 0 16px;
  font-size: 20px;
  font-weight: 700;
  font-family: inherit;
  color: #0f172a;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.2s;

  &:focus {
    border-color: #2dbcaf;
  }

  &::placeholder {
    color: #cbd5e1;
    font-weight: 500;
    font-size: 16px;
  }
`;

const ErrorHint = styled.div`
  font-size: 12px;
  color: #ef4444;
  margin-top: 6px;
`;

const PresetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-top: 12px;
`;

const PresetButton = styled.button<{ $active: boolean }>`
  padding: 10px 0;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s;
  border: 2px solid ${(p) => (p.$active ? '#2dbcaf' : '#e2e8f0')};
  background: ${(p) => (p.$active ? 'rgba(45, 188, 175, 0.08)' : 'white')};
  color: ${(p) => (p.$active ? '#2dbcaf' : '#475569')};

  &:active {
    transform: scale(0.97);
  }
`;

const MethodGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  
  @media (max-width: 400px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const MethodCard = styled.button<{ $active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 16px 12px;
  border-radius: 14px;
  border: 2px solid ${(p) => (p.$active ? '#2dbcaf' : '#e2e8f0')};
  background: ${(p) => (p.$active ? 'rgba(45, 188, 175, 0.08)' : 'white')};
  cursor: pointer;
  transition: all 0.15s;
  font-family: inherit;

  &:active {
    transform: scale(0.97);
  }
`;

const MethodIcon = styled.div`
  font-size: 28px;
`;

const MethodName = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
`;

const MethodDesc = styled.div`
  font-size: 11px;
  color: #94a3b8;
`;

const ErrorBox = styled.div`
  margin-top: 16px;
  padding: 12px 14px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 12px;
  font-size: 13px;
  color: #dc2626;
`;

const PayButton = styled.button`
  width: 100%;
  height: 52px;
  margin-top: 20px;
  border-radius: 14px;
  border: none;
  background: linear-gradient(135deg, #2dbcaf, #059669);
  color: white;
  font-size: 16px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:not(:disabled):active {
    transform: scale(0.98);
  }
`;

/* Payment Section (pending state) */
const PaymentSection = styled.section`
  background: white;
  border-radius: 20px;
  padding: 24px;
  margin-top: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
`;

const Title = styled.h3`
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 6px;
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0 0 20px;
  text-align: center;
`;

/* QRIS */
const QRISBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: #f8fafc;
  border-radius: 16px;
  margin-bottom: 16px;
`;

const QRISImage = styled.img`
  width: 220px;
  height: 220px;
  object-fit: contain;
`;

const QRISPlaceholder = styled.div`
  width: 220px;
  height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 14px;
`;

/* VA */
const VABox = styled.div`
  text-align: center;
  padding: 24px;
  background: #f8fafc;
  border-radius: 16px;
  margin-bottom: 16px;
`;

const VABankLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 8px;
`;

const VANumberDisplay = styled.div`
  font-size: 28px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: 2px;
  font-family: monospace;
  margin-bottom: 12px;
  word-break: break-all;
`;

const CopyButton = styled.button`
  padding: 8px 20px;
  background: #2dbcaf;
  color: white;
  font-size: 13px;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-family: inherit;

  &:active {
    transform: scale(0.97);
  }
`;

const VASteps = styled.div`
  background: #f8fafc;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
`;

const StepTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #475569;
  margin-bottom: 10px;
`;

const Step = styled.div`
  font-size: 12px;
  color: #64748b;
  line-height: 2;
`;

/* Shared payment UI */
const AmountBox = styled.div`
  text-align: center;
  margin-bottom: 16px;
`;

const AmountLabel = styled.div`
  font-size: 13px;
  color: #64748b;
  margin-bottom: 4px;
`;

const AmountValue = styled.div`
  font-size: 28px;
  font-weight: 800;
  color: #2dbcaf;
`;

const InfoBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 12px;
  padding: 12px 14px;
  margin-bottom: 16px;
`;

const InfoIcon = styled.span`
  font-size: 16px;
`;

const InfoText = styled.p`
  font-size: 12px;
  color: #92400e;
  margin: 0;
  line-height: 1.5;
`;

const SupportedApps = styled.div`
  text-align: center;
  margin-bottom: 16px;
`;

const SupportedLabel = styled.div`
  font-size: 11px;
  color: #94a3b8;
  margin-bottom: 8px;
`;

const AppList = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
`;

const AppBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  color: #64748b;
  background: #f1f5f9;
  padding: 4px 10px;
  border-radius: 20px;
`;

const StatusBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 12px;
  background: linear-gradient(
    135deg,
    rgba(45, 188, 175, 0.1),
    rgba(45, 188, 175, 0.05)
  );
  border-radius: 12px;
  margin-bottom: 12px;
`;

const PulsingDot = styled.div`
  width: 10px;
  height: 10px;
  background: #2dbcaf;
  border-radius: 50%;
  animation: pulse 1.5s infinite;

  @keyframes pulse {
    0% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.5; transform: scale(1.2); }
    100% { opacity: 1; transform: scale(1); }
  }
`;

const StatusText = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #2dbcaf;
`;

const CountdownText = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: #64748b;
  font-family: monospace;
`;

const OrderInfo = styled.div`
  text-align: center;
  font-size: 11px;
  color: #94a3b8;
  margin-bottom: 16px;

  strong {
    color: #64748b;
  }
`;

const CancelButton = styled.button`
  width: 100%;
  height: 48px;
  border-radius: 14px;
  font-size: 14px;
  font-weight: 700;
  font-family: inherit;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  background: white;
  color: #64748b;

  &:active {
    background: #f8fafc;
  }
`;

/* State boxes */
const StateBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
`;

const StateIcon = styled.div`
  font-size: 56px;
  margin-bottom: 16px;
`;

const StateTitle = styled.h3`
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 8px;
`;

const StateText = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0;
`;

const RetryButton = styled.button`
  margin-top: 20px;
  padding: 12px 32px;
  background: #2dbcaf;
  color: white;
  font-size: 14px;
  font-weight: 700;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-family: inherit;

  &:active {
    transform: scale(0.98);
  }
`;

const Spinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid #e2e8f0;
  border-top-color: #2dbcaf;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;
